import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { creditLimitsApi } from '../services/creditLimitsApi';
import { DEFAULT_NEAR_LIMIT_PCT, type CreditLimitCallerRoles, type OverviewData, type OverviewResponse, type OverviewScope } from '../types/creditLimits';

export interface UseCreditLimitsReturn {
    /** The overview with the near-limit threshold of the config (`nearLimitPct`, E30). */
    overview: OverviewData | null;
    /** Roles of the signed-in person; null until the first lookup succeeds (see `lookupError`). */
    roles: CreditLimitCallerRoles | null;
    loading: boolean;
    /** The last overview request failed: the previous overview (if any) stays on screen. */
    error: string | null;
    /** A lookup failed (roles): the page works, but rights that depend on it are off. Shown, never swallowed. */
    lookupError: string | null;
    refresh: () => void;
}

function messageOf(reason: unknown, fallback: string): string {
    return reason instanceof Error && reason.message ? reason.message : fallback;
}

/** The config's `settings.nearLimitPct` when it is a whole percentage of 1..100, else the default. */
export function nearLimitPctOf(config: unknown): number {
    const v = (config as { settings?: { nearLimitPct?: unknown } } | null | undefined)?.settings?.nearLimitPct;
    return typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 100 ? v : DEFAULT_NEAR_LIMIT_PCT;
}

/**
 * Loads the overview of one scope and the caller's roles. The two requests run in parallel with `Promise.allSettled`
 * (a failing lookup must not hide the grid), and a rejection is reported in `error` / `lookupError`: an empty list
 * can be a 500 (CLAUDE.md, learning «empty dropdown»). The config also carries `NearLimitPct` (E30); it is handed on
 * inside `overview` so the bands and tiles never need a second request.
 */
export function useCreditLimits(scope: OverviewScope): UseCreditLimitsReturn {
    const [response, setResponse] = useState<OverviewResponse | null>(null);
    const [roles, setRoles] = useState<CreditLimitCallerRoles | null>(null);
    const [nearLimitPct, setNearLimitPct] = useState(DEFAULT_NEAR_LIMIT_PCT);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lookupError, setLookupError] = useState<string | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const counter = useRef(0);

    const rolesLoaded = useRef(false);

    useEffect(() => {
        const mine = ++counter.current;
        setLoading(true);
        setError(null);

        // The caller's roles are asked for once per page load (and again on a retry while they are still missing):
        // a scope switch must not re-ask, and a later failure must not wipe roles that are already loaded.
        const wantRoles = !rolesLoaded.current;
        Promise.allSettled([
            creditLimitsApi.getOverview(scope),
            wantRoles ? creditLimitsApi.getConfig() : Promise.resolve(null),
        ]).then(([ov, cfg]) => {
            if (mine !== counter.current) return;
            if (ov.status === 'fulfilled') {
                setResponse(ov.value);
            } else {
                // Keep the last overview: the toolbar and the scope switch stay, so the way back needs no reload.
                setError(messageOf(ov.reason, 'Failed to load credit limits'));
            }
            if (cfg.status === 'fulfilled') {
                if (cfg.value) {
                    rolesLoaded.current = true;
                    setRoles(cfg.value.roles);
                    setNearLimitPct(nearLimitPctOf(cfg.value));
                    setLookupError(null);
                }
            } else {
                setLookupError(messageOf(cfg.reason, 'Failed to load your roles'));
            }
            setLoading(false);
        });
    }, [scope, refreshTrigger]);

    const refresh = useCallback(() => setRefreshTrigger(n => n + 1), []);

    const overview = useMemo<OverviewData | null>(
        () => (response ? { ...response, nearLimitPct } : null),
        [response, nearLimitPct],
    );

    return { overview, roles, loading, error, lookupError, refresh };
}
